/* =============================================================
   landingCopy.js — bilingual marketing copy for the redesigned
   landing page showcase sections.

   We keep this co-located instead of bloating the shared
   LanguageContext translations object: all brand-new marketing
   strings live here, selected by the active language. Existing
   t() keys (hero, benefits, faq, about…) are still read from
   LanguageContext so nothing regresses.
   ============================================================= */

const LANDING_COPY = {
  en: {
    // Hero
    heroEyebrow: 'BSC Mainnet · Real USDT · Audited Contracts',
    heroTitle: 'Own the machine.\nStream the future.',
    heroLede:
      'PhoneStream turns music streaming into a living, on-chain economy. Mint an NFT, earn real USDT from streams, grow any social platform, and play a full neon casino — all from one wallet.',
    ctaConnect: 'Connect Wallet',
    ctaExplore: 'Explore the Platform',
    heroNote: 'No sign-up forms. Connect a wallet and you are in.',

    // Live stat band
    statVolume: 'USDT Volume Settled',
    statSupply: 'NFT Collection Supply',
    statGames: 'Casino Games Live',
    statPlatforms: 'SMM Platforms',
    statChain: 'Settlement Chain',
    statUptime: 'Streaming Uptime',

    // Showcase – section eyebrow
    showcaseEyebrow: 'Inside the platform',
    showcaseTitle: 'See exactly what you unlock',
    showcaseSubtitle:
      'A quick look at the live product — the dashboard, the SMM engine, the marketplace and the games are waiting the moment your wallet connects.',

    // Platforms strip
    platformsEyebrow: 'Every platform, one wallet',
    platformsTitle: 'Grow where your audience already is',

    // Dashboard showcase
    dashEyebrow: 'Your command center',
    dashTitle: 'A fintech-grade dashboard for your streams',
    dashBody:
      'Track lifetime streaming revenue, PlayVault balance, SMM escrow and every order in real time. Bloomberg-style live tickers, KPI tiles and 14-day spend charts — refreshed every few seconds.',
    dashB1: 'Live revenue + balance tickers',
    dashB2: 'KPI tiles with 24h deltas',
    dashB3: '14-day spending analytics',
    dashB4: 'Orders & purchases history',

    // SMM showcase
    smmEyebrow: 'Grow anything, pay in USDT',
    smmTitle: 'A full social-marketing engine, built in',
    smmBody:
      'Order followers, views, plays and engagement across 12+ platforms — Instagram, YouTube, TikTok, Spotify, Telegram and more. Funds sit in on-chain escrow; orders track live until delivered.',
    smmB1: 'Instagram · YouTube · TikTok · Spotify',
    smmB2: 'USDT escrow — no withdrawals lost',
    smmB3: 'Live order status & refills',
    smmB4: 'Holder discounts auto-applied',

    // Marketplace showcase
    mktEyebrow: 'Own a piece of the machine',
    mktTitle: 'A generative NFT marketplace on BSC',
    mktBody:
      'Each NFT is a share of a 24/7 streaming machine tied to a real artist. Watch mint price, total volume and supply fill in real time, then browse the full collection.',
    mktB1: 'Live mint price & volume',
    mktB2: '1,000-piece capped collection',
    mktB3: 'Revenue tied to your NFT',
    mktB4: 'Transparent on-chain ownership',

    // Casino showcase
    casEyebrow: 'Play the vault',
    casTitle: 'A neon casino with real USDT stakes',
    casBody:
      'Ten provably-styled games run straight from your wallet — Mines, Crash, Plinko, Keno, Roulette, Blackjack, Video Poker, Coin Flip, Dice and the Wheel. Cash out any time.',
    casB1: 'Mines · Crash · Plinko · Keno',
    casB2: 'Blackjack & Video Poker tables',
    casB3: 'Instant USDT cash-outs',
    casB4: 'Provably-fair payouts',

    // How it works
    howEyebrow: 'From zero to earning',
    howTitle: 'New to crypto? You are four steps away.',
    howSubtitle:
      'No bank, no paperwork, no middlemen. A wallet is a free app on your phone — here is the whole journey.',
    step1Title: 'Get a wallet',
    step1Body:
      'Install a free wallet like MetaMask or Trust Wallet. It takes two minutes and lives on your phone or browser.',
    step2Title: 'Connect & join',
    step2Body:
      'Tap Connect Wallet, sign one free message, and your private PhoneStream dashboard opens instantly.',
    step3Title: 'Mint or fund',
    step3Body:
      'Mint an NFT to earn streaming revenue, or top up USDT to run SMM orders and play the games.',
    step4Title: 'Earn & cash out',
    step4Body:
      'Watch revenue land in your dashboard, win on the games, and withdraw real USDT to your wallet.',

    // Final CTA
    finalEyebrow: 'The doors are open',
    finalTitle: 'Your wallet is the only key you need',
    finalBody:
      'Join the holders already earning from streams, marketing and the vault. It is free to look inside — connect and see your dashboard light up.',
    finalCta: 'Connect Wallet & Enter',
    finalSub: 'Built on Binance Smart Chain · Settled in real USDT',

    // Generic
    learnMore: 'Learn more',
    livePreview: 'Live preview',
  },

  es: {
    heroEyebrow: 'BSC Mainnet · USDT Real · Contratos Auditados',
    heroTitle: 'Posee la máquina.\nTransmite el futuro.',
    heroLede:
      'PhoneStream convierte el streaming de música en una economía viva on-chain. Mintea un NFT, gana USDT real con los streams, haz crecer cualquier red social y juega un casino neón completo — todo desde una sola billetera.',
    ctaConnect: 'Conectar Billetera',
    ctaExplore: 'Explorar la Plataforma',
    heroNote: 'Sin formularios. Conecta una billetera y estás dentro.',

    statVolume: 'Volumen USDT Liquidado',
    statSupply: 'Suministro de la Colección NFT',
    statGames: 'Juegos de Casino Activos',
    statPlatforms: 'Plataformas SMM',
    statChain: 'Cadena de Liquidación',
    statUptime: 'Disponibilidad de Streaming',

    showcaseEyebrow: 'Dentro de la plataforma',
    showcaseTitle: 'Mira exactamente lo que desbloqueas',
    showcaseSubtitle:
      'Un vistazo rápido al producto en vivo — el panel, el motor SMM, el mercado y los juegos te esperan en el momento en que conectas tu billetera.',

    // Platforms strip
    platformsEyebrow: 'Cada plataforma, una billetera',
    platformsTitle: 'Crece donde tu audiencia ya está',

    dashEyebrow: 'Tu centro de control',
    dashTitle: 'Un panel de nivel fintech para tus streams',
    dashBody:
      'Rastrea ingresos de streaming de por vida, saldo de PlayVault, escrow de SMM y cada pedido en tiempo real. Tickers en vivo estilo Bloomberg, KPIs y gráficos de gasto de 14 días — actualizados cada pocos segundos.',
    dashB1: 'Tickers de ingresos y saldo en vivo',
    dashB2: 'KPIs con variación de 24h',
    dashB3: 'Analíticas de gasto de 14 días',
    dashB4: 'Historial de pedidos y compras',

    smmEyebrow: 'Haz crecer todo, paga en USDT',
    smmTitle: 'Un motor de marketing social completo, integrado',
    smmBody:
      'Pide seguidores, vistas, reproducciones y engagement en más de 12 plataformas — Instagram, YouTube, TikTok, Spotify, Telegram y más. Los fondos quedan en escrow on-chain; los pedidos se rastrean en vivo hasta entregarse.',
    smmB1: 'Instagram · YouTube · TikTok · Spotify',
    smmB2: 'Escrow USDT — sin retiros perdidos',
    smmB3: 'Estado de pedidos y recargas en vivo',
    smmB4: 'Descuentos para holders automáticos',

    mktEyebrow: 'Posee una parte de la máquina',
    mktTitle: 'Un mercado de NFT generativos en BSC',
    mktBody:
      'Cada NFT es una parte de una máquina de streaming 24/7 ligada a un artista real. Observa el precio de minteo, el volumen total y el suministro llenarse en tiempo real, y explora la colección completa.',
    mktB1: 'Precio de minteo y volumen en vivo',
    mktB2: 'Colección limitada de 1,000 piezas',
    mktB3: 'Ingresos ligados a tu NFT',
    mktB4: 'Propiedad transparente on-chain',

    casEyebrow: 'Juega la bóveda',
    casTitle: 'Un casino neón con apuestas en USDT real',
    casBody:
      'Diez juegos corren directo desde tu billetera — Mines, Crash, Plinko, Keno, Ruleta, Blackjack, Video Poker, Cara o Cruz, Dados y la Rueda. Retira cuando quieras.',
    casB1: 'Mines · Crash · Plinko · Keno',
    casB2: 'Mesas de Blackjack y Video Poker',
    casB3: 'Retiros instantáneos en USDT',
    casB4: 'Pagos provably-fair',

    howEyebrow: 'De cero a ganar',
    howTitle: '¿Nuevo en cripto? Estás a cuatro pasos.',
    howSubtitle:
      'Sin banco, sin papeleo, sin intermediarios. Una billetera es una app gratis en tu teléfono — este es todo el recorrido.',
    step1Title: 'Consigue una billetera',
    step1Body:
      'Instala una billetera gratis como MetaMask o Trust Wallet. Toma dos minutos y vive en tu teléfono o navegador.',
    step2Title: 'Conecta y únete',
    step2Body:
      'Toca Conectar Billetera, firma un mensaje gratis, y tu panel privado de PhoneStream se abre al instante.',
    step3Title: 'Mintea o financia',
    step3Body:
      'Mintea un NFT para ganar ingresos de streaming, o recarga USDT para hacer pedidos SMM y jugar.',
    step4Title: 'Gana y retira',
    step4Body:
      'Observa los ingresos llegar a tu panel, gana en los juegos y retira USDT real a tu billetera.',

    finalEyebrow: 'Las puertas están abiertas',
    finalTitle: 'Tu billetera es la única llave que necesitas',
    finalBody:
      'Únete a los holders que ya ganan con streams, marketing y la bóveda. Mirar dentro es gratis — conecta y mira tu panel encenderse.',
    finalCta: 'Conectar Billetera y Entrar',
    finalSub: 'Construido en Binance Smart Chain · Liquidado en USDT real',

    learnMore: 'Saber más',
    livePreview: 'Vista previa en vivo',
  },
};

export default LANDING_COPY;
